// Lectura del contenedor .ork (ZIP, XML plano o gzip) con límites estrictos. Ver docs/theory/ork-format.md.
import { Gunzip, Unzip, UnzipInflate, UnzipPassThrough, strFromU8 } from 'fflate';

export interface ArchiveLimits {
  /** Tamaño máximo del archivo comprimido (bytes). */
  readonly maxFileBytes: number;
  /** Tamaño máximo del XML ya descomprimido (bytes). */
  readonly maxXmlBytes: number;
  /** Número máximo de entradas del ZIP. */
  readonly maxEntries: number;
}

export const DEFAULT_LIMITS: ArchiveLimits = {
  maxFileBytes: 25_000_000,
  maxXmlBytes: 60_000_000,
  maxEntries: 500,
};

export type ArchiveResult = { readonly ok: true; readonly xml: string } | { readonly ok: false; readonly error: string };

const fail = (error: string): ArchiveResult => ({ ok: false, error });
const mb = (n: number): string => `${Math.round(n / 1_000_000)} MB`;

function join(chunks: Uint8Array[], total: number): Uint8Array {
  const out = new Uint8Array(total);
  let o = 0;
  for (const c of chunks) {
    out.set(c, o);
    o += c.length;
  }
  return out;
}

function decode(bytes: Uint8Array): ArchiveResult {
  const text = strFromU8(bytes).replace(/^﻿/, '');
  return text.trimStart().startsWith('<') ? { ok: true, xml: text } : fail('El contenido no es un documento XML');
}

function readZip(bytes: Uint8Array, limits: ArchiveLimits): ArchiveResult {
  const chunks: Uint8Array[] = [];
  let total = 0;
  let entries = 0;
  let chosen = false;
  let failure: string | undefined;

  const unzip = new Unzip();
  unzip.register(UnzipInflate);
  unzip.register(UnzipPassThrough);
  unzip.onfile = (file) => {
    entries++;
    if (entries > limits.maxEntries) {
      failure ??= `El archivo tiene demasiadas entradas (máximo ${limits.maxEntries})`;
      return;
    }
    const name = file.name;
    const isRoot = !name.includes('/') && !name.includes('\\');
    if (chosen || !isRoot || !/\.(ork|xml)$/i.test(name)) return;
    chosen = true;
    file.ondata = (err, data) => {
      if (err !== null) {
        failure ??= 'El archivo está dañado: no se pudo descomprimir';
        return;
      }
      total += data.length;
      if (total > limits.maxXmlBytes) {
        failure ??= `El contenido descomprimido supera el máximo permitido (${mb(limits.maxXmlBytes)})`;
        file.terminate();
        return;
      }
      chunks.push(data);
    };
    file.start();
  };

  try {
    unzip.push(bytes, true);
  } catch {
    return fail('El archivo está dañado: no es un ZIP válido');
  }
  if (failure !== undefined) return fail(failure);
  if (!chosen) return fail('El archivo ZIP no contiene un documento rocket.ork');
  return decode(join(chunks, total));
}

function readGzip(bytes: Uint8Array, limits: ArchiveLimits): ArchiveResult {
  const chunks: Uint8Array[] = [];
  let total = 0;
  let failure: string | undefined;
  const gz = new Gunzip((chunk) => {
    total += chunk.length;
    if (total > limits.maxXmlBytes) {
      failure ??= `El contenido descomprimido supera el máximo permitido (${mb(limits.maxXmlBytes)})`;
      throw new Error('límite de tamaño descomprimido'); // aborta la descompresión
    }
    chunks.push(chunk);
  });
  try {
    gz.push(bytes, true);
  } catch {
    return fail(failure ?? 'El archivo está dañado: no es un gzip válido');
  }
  if (failure !== undefined) return fail(failure);
  return decode(join(chunks, total));
}

/** Extrae el XML del diseño. Nunca lanza: los problemas se devuelven como error descriptivo. */
export function readOrkXml(bytes: Uint8Array, limits: ArchiveLimits = DEFAULT_LIMITS): ArchiveResult {
  if (bytes.length === 0) return fail('El archivo está vacío');
  if (bytes.length > limits.maxFileBytes) return fail(`El archivo supera el tamaño máximo permitido (${mb(limits.maxFileBytes)})`);
  if (bytes[0] === 0x50 && bytes[1] === 0x4b) return readZip(bytes, limits);
  if (bytes[0] === 0x1f && bytes[1] === 0x8b) return readGzip(bytes, limits);
  if (bytes.length > limits.maxXmlBytes) return fail(`El contenido supera el máximo permitido (${mb(limits.maxXmlBytes)})`);
  return decode(bytes);
}
